import { Heart, MessageCircle, Mail, Instagram, Settings, ArrowUpRight, MapPin } from "lucide-react";

const navLinks = [
  { href: "/excursions", label: "Excursions" },
  { href: "/blog", label: "Blog" },
  { href: "/a-propos", label: "À propos" },
  { href: "/avis", label: "Avis" },
  { href: "/contact", label: "Contact" },
];

const socialLinks = [
  { href: "https://wa.me/+5521976795050", label: "WhatsApp", icon: MessageCircle, external: true },
  { href: "https://instagram.com/roca.rio_", label: "@roca.rio_", icon: Instagram, external: true },
  { href: "https://instagram.com/meninos.dobrasil", label: "@meninos.dobrasil", icon: Instagram, external: true },
  { href: "mailto:contact.rocario@gmail.com", label: "Email", icon: Mail, external: false },
];

const year = new Date().getFullYear();

export const Footer = () => {
  return (
    <footer className="relative bg-roca-blue-dark text-white overflow-hidden" role="contentinfo" aria-label="Pied de page">
      {/* Decorative glows */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-roca-orange/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-primary/15 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-8">
        {/* Top · Big editorial statement */}
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 pb-12 sm:pb-16 border-b border-white/10">
          <div>
            <a href="/" aria-label="Accueil Roca Rio" className="inline-flex items-center justify-center bg-roca-cream rounded-xl p-3 mb-5 hover:scale-[1.02] transition-transform">
              <img src="/roca-rio-logo.png" alt="Roca Rio" className="h-14 w-auto" />
            </a>
            <p className="text-xs uppercase tracking-[0.3em] text-roca-orange mb-4">Roca Rio</p>
            <h2 className="font-league uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.95] mb-6">
              Vivez Rio <span className="text-gradient-roca">autrement</span>,<br />
              voyagez avec du <span className="text-gradient-roca">sens</span>.
            </h2>
            <p className="text-white/70 max-w-lg leading-relaxed mb-6">
              Guide francophone certifiée à Rio de Janeiro. Excursions authentiques, petits groupes, tourisme solidaire.
            </p>
            <a
              href="/contact"
              className="group inline-flex items-center gap-3 text-white font-medium hover:text-roca-orange transition-colors"
            >
              Réserver une excursion
              <span className="w-8 h-px bg-current group-hover:w-12 transition-all" />
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
          </div>

          <div className="grid grid-cols-2 gap-6 sm:gap-8">
            <div>
              <h4 className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Navigation</h4>
              <ul className="space-y-2.5">
                {navLinks.map((l) => (
                  <li key={l.href}>
                    <a href={l.href} className="text-sm text-white/85 hover:text-roca-orange transition-colors">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Suivez-moi</h4>
              <ul className="space-y-2.5">
                {socialLinks.map(({ href, label, icon: Icon, external }) => (
                  <li key={label}>
                    <a
                      href={href}
                      target={external ? "_blank" : undefined}
                      rel={external ? "noopener noreferrer" : undefined}
                      className="group inline-flex items-center gap-2 text-xs sm:text-sm text-white/85 hover:text-roca-orange transition-colors break-all"
                    >
                      <Icon className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100" />
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Middle · Brand bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 py-8">
          <div className="flex items-center gap-4">
            <img
              src="/lovable-uploads/2b4cc97e-392f-4df4-85e5-ce8b66a05815.png?width=150"
              alt="Roca Rio"
              className="h-8 w-auto"
              width="1536"
              height="1024"
            />
            <div className="hidden sm:flex items-center gap-2 text-xs text-white/60">
              <MapPin className="w-3.5 h-3.5" />
              <span>Rio de Janeiro · Brésil</span>
            </div>
          </div>
          <div className="inline-flex w-full md:w-auto justify-center items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-xs text-white/80 self-start md:self-auto">
            <Heart className="w-3.5 h-3.5 text-roca-orange fill-roca-orange" />
            Soutient l'association <span className="font-semibold">Meninos do Brasil</span>
          </div>
        </div>

        {/* Bottom · Legal */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-xs">
            © {year} Roca Rio · Tous droits réservés.
          </p>
          <nav aria-label="Liens légaux" className="flex flex-wrap justify-center items-center gap-x-5 gap-y-2 text-xs">
            <a href="/mentions-legales" className="text-white/50 hover:text-roca-orange transition-colors">Mentions légales</a>
            <a href="/confidentialite" className="text-white/50 hover:text-roca-orange transition-colors">Confidentialité</a>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openCookieSettings'))}
              className="text-white/50 hover:text-roca-orange transition-colors"
              aria-label="Gérer les préférences des cookies"
            >
              Cookies
            </button>
            <a href="/admin" className="inline-flex items-center gap-1 text-white/40 hover:text-roca-orange transition-colors" aria-label="Admin">
              <Settings className="w-3 h-3" /> Admin
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
};